import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clapperboard, Loader2, Lock } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F17] px-4 relative overflow-hidden">
      <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-violet-600/10 blur-[120px] pointer-events-none" />
      <div className="w-full max-w-md relative animate-in-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-900/40">
            <Clapperboard className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-display font-extrabold text-slate-50 leading-tight">YouTube Studio OS</h1>
            <p className="text-xs text-slate-500">Multi-channel production, on autopilot</p>
          </div>
        </div>

        <div className="bg-[#111827] border border-slate-800/80 rounded-2xl p-7 shadow-2xl shadow-black/40">
          <h2 className="text-xl font-display font-bold text-slate-100 mb-1">Welcome back</h2>
          <p className="text-sm text-slate-400 mb-6">Sign in to your workspace.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300 text-sm mb-1.5 block">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="owner@studioos.app" data-testid="login-email-input"
                className="bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300 text-sm mb-1.5 block">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="login-password-input"
                className="bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30" />
            </div>
            {error && (
              <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={busy} data-testid="login-submit-button"
              className="w-full bg-violet-600 hover:bg-violet-500 text-white font-medium">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" /> Sign in</>}
            </Button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-600 mt-6">Single-owner workspace · no public sign-up</p>
      </div>
    </div>
  );
}
